import { useLazyLoadQuery } from 'react-relay';
import { PDP_DESKTOP_AGGREGATION_FEB_2025 } from '../../../constants/abTestsConstants.ts';
import { type FC, type ComponentProps, useEffect } from 'react';
import {
    pdpRespMarketplacePrimaryRootQuery,
    type PdpRespMarketplacePrimaryRootQueryType,
    type pdpRespMarketplacePrimaryRootQuery$variables as PrimaryRelayVariables,
} from '../../../queries/pdpRespMarketplacePrimaryRoot.ts';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2.ts';
import { DblExternalContextProvider } from 'dibs-buyer-layout/exports/DblExternalContext.ts';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { trackItemFavoritingFeb2026Test } from 'dibs-buyer-item-favoriting/src/abTests.ts';

// components
import { Provider } from 'react-redux';
import { IntlProvider } from 'dibs-react-intl';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import { Error500Layout } from 'dibs-buyer-layout/exports/error500.tsx';
import { ToastProvider } from 'dibs-toaster/exports/ToastProvider.ts';
import { PdpRespMarketplaceLayoutPrimary } from './PdpRespMarketplaceLayoutPrimary.tsx';
import { PdpRespMarketplaceLayoutSecondaryRenderer } from './PdpRespMarketplaceLayoutSecondaryRenderer.tsx';
import { PersonalizedCollectionsProvider } from 'dibs-personalized-collections/exports/PersonalizedCollectionsContext.ts';

// types
import { type Store } from 'redux';
import { type Locale } from 'dibs-intl/exports/locales';
import { type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables } from '../../../queries/pdpRespMarketplaceSecondaryRoot.ts';

type IntlProviderProps = ComponentProps<typeof IntlProvider>;
type Messages = IntlProviderProps['messages'];

export const PdpRespMarketplaceLayout: FC<{
    store: Store;
    locale: Locale;
    messages: Messages;
    primaryRelayVariables: PrimaryRelayVariables;
    secondaryRelayVariables: SecondaryRelayVariables;
}> = ({ locale, messages, primaryRelayVariables, secondaryRelayVariables, store }) => {
    const primaryData = useLazyLoadQuery<PdpRespMarketplacePrimaryRootQueryType>(
        pdpRespMarketplacePrimaryRootQuery,
        primaryRelayVariables,
        { fetchPolicy: 'store-only' }
    );

    useDwelledOnPage(PAGE_TYPE.PDP);

    // test tracking
    useEffect(() => {
        trackAbTestV2Variant(PDP_DESKTOP_AGGREGATION_FEB_2025);
        trackItemFavoritingFeb2026Test();
    }, []);

    return (
        <IntlProvider locale={locale} messages={messages}>
            <ErrorBoundary fallback={({ error }) => <Error500Layout error={error} />}>
                <ToastProvider trackItemFavoriting>
                    <PersonalizedCollectionsProvider item={primaryData.viewer?.itemRead?.[0]}>
                        <Provider store={store}>
                            <PdpRespMarketplaceLayoutPrimary viewer={primaryData.viewer} />
                        </Provider>
                        <DblExternalContextProvider store={store}>
                            <PdpRespMarketplaceLayoutSecondaryRenderer
                                relayVariables={secondaryRelayVariables}
                                ssr={!!primaryRelayVariables.afterDisplayOrBtSsr}
                            />
                        </DblExternalContextProvider>
                    </PersonalizedCollectionsProvider>
                </ToastProvider>
            </ErrorBoundary>
        </IntlProvider>
    );
};
